import AccountCircle from "@mui/icons-material/AccountCircle";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Container,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Link as BaseLink, useNavigate } from "react-router-dom";
import Logo from "@assets/navbar-logo.svg?react";
import { Locale } from "i18n/config";
import useLocale from "@utils/useLocale";
import { emailAuth } from "@utils/emailAuthUtils.ts";

export default function NavBar() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawer] = useState(false);
  const isEntraAuthenticated = useIsAuthenticated();
  const isEmailAuthenticated = !emailAuth.isTokenExpired();
  const isAuthenticated = isEntraAuthenticated || isEmailAuthenticated;

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    if (isEmailAuthenticated) {
      emailAuth.removeJwtToken();
      handleCloseUserMenu();
      void navigate("/login");
    } else {
      void instance
        .logoutPopup()
        .then(() => {
          handleCloseUserMenu();
          void navigate("/login");
        })
        .catch((e: unknown) => {
          console.error(e);
        });
    }
  };

  const { locale, setLocale } = useLocale();

  const handleLocaleChange = (locale: Locale) => {
    void setLocale(locale);
    handleCloseUserMenu();
  };
  const drawerLinks = [[t("navbar.drawer.firstPage"), "/"]];
  if (isAuthenticated) {
    drawerLinks.push(
      [
        t("navbar.drawer.shop"),
        isEmailAuthenticated ? "/invitation/purchase" : "/purchase",
      ],
      [
        t("navbar.drawer.userInfo"),
        isEmailAuthenticated ? "/invitation/user" : "/user",
      ],
    );
  }

  const showDrawer = (isShown: boolean) => () => {
    setDrawer(isShown);
  };

  const handleNavigate = (path: string) => {
    handleCloseUserMenu();
    void navigate(path);
  };

  const drawerContent = (
    <Stack padding={4}>
      <Typography variant="h6" noWrap>
        {t("navbar.title")}
      </Typography>
      <List>
        {drawerLinks.map(([text, link]) => (
          <ListItem key={text} onClick={showDrawer(false)}>
            <ListItemButton
              onClick={() => {
                handleNavigate(link);
              }}
            >
              <Typography>{text}</Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Typography variant="h6" noWrap>
        {t("navbar.drawer.helpdesk")}
      </Typography>
      <List>
        <ListItem key="contactInfo" onClick={showDrawer(false)}>
          <ListItemButton
            onClick={() => {
              handleNavigate("/contact-info");
            }}
          >
            <Typography>{t("navbar.drawer.contactInformation")}</Typography>
          </ListItemButton>
        </ListItem>
        <ListItem key="faq">
          <Link
            href="https://pohde.zendesk.com/hc/fi-fi"
            color="inherit"
            underline="none"
          >
            <ListItemButton>
              <Typography>{t("navbar.drawer.faq")}</Typography>
            </ListItemButton>
          </Link>
        </ListItem>
      </List>
      <Typography variant="h6" noWrap>
        {t("navbar.drawer.termsAndConditions")}
      </Typography>
      <List>
        <ListItem key="privacyPolicy" onClick={showDrawer(false)}>
          <ListItemButton
            onClick={() => {
              handleNavigate("/privacy-policy");
            }}
          >
            <Typography>{t("navbar.drawer.privacyPolicy")}</Typography>
          </ListItemButton>
        </ListItem>
        <ListItem key="paymentPolicy" onClick={showDrawer(false)}>
          <ListItemButton
            onClick={() => {
              handleNavigate("/payment-policy");
            }}
          >
            <Typography>{t("navbar.drawer.paymentPolicy")}</Typography>
          </ListItemButton>
        </ListItem>
      </List>
    </Stack>
  );

  return (
    <AppBar position="static">
      <Container>
        <Toolbar disableGutters>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Stack direction="row" spacing={5}>
              <Link
                component={BaseLink}
                to="/"
                sx={{
                  color: "inherit",
                  display: "flex",
                  gap: 1,
                }}
              >
                <Logo aria-hidden />
              </Link>
              <IconButton
                onClick={showDrawer(true)}
                sx={{ p: 0, color: "inherit" }}
              >
                <MenuIcon fontSize="large" />
              </IconButton>
              <Drawer open={drawerOpen} onClose={showDrawer(false)}>
                {drawerContent}
              </Drawer>
            </Stack>
            <Stack>
              <Tooltip title={t("navbar.menuTooltip")}>
                <IconButton
                  onClick={handleOpenUserMenu}
                  sx={{ p: 0, color: "inherit" }}
                >
                  <AccountCircle fontSize="large" />
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem
                  selected={locale === "fi"}
                  onClick={() => {
                    handleLocaleChange("fi");
                  }}
                >
                  <Typography sx={{ textAlign: "center" }}>
                    {t("navbar.finnish")}
                  </Typography>
                </MenuItem>
                <MenuItem
                  selected={locale === "en"}
                  onClick={() => {
                    handleLocaleChange("en");
                  }}
                >
                  <Typography sx={{ textAlign: "center" }}>
                    {t("navbar.english")}
                  </Typography>
                </MenuItem>
                <MenuItem
                  selected={locale === "sv"}
                  onClick={() => {
                    handleLocaleChange("sv");
                  }}
                >
                  <Typography sx={{ textAlign: "center" }}>
                    {t("navbar.swedish")}
                  </Typography>
                </MenuItem>
                {
                  // Menu doesn't allow fragments as children
                  isAuthenticated && <Divider />
                }
                {isAuthenticated && (
                  <MenuItem
                    onClick={() => {
                      handleNavigate(
                        isEmailAuthenticated ? "/invitation/user" : "/user",
                      );
                    }}
                  >
                    <ManageAccountsIcon aria-hidden />
                    <Typography sx={{ textAlign: "center" }}>
                      {t("navbar.userDetails")}
                    </Typography>
                  </MenuItem>
                )}
                {isAuthenticated && (
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon aria-hidden />
                    <Typography sx={{ textAlign: "center" }}>
                      {t("navbar.logout")}
                    </Typography>
                  </MenuItem>
                )}
              </Menu>
            </Stack>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
